import { useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { DateTime } from "luxon";
import { useQuery } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

import { Button } from "../../components/Buttons/Button";
import { ModalActions } from "../../components/Modals/ModalActions";
import { getContrastTextColor } from "../../utilities/helpers/getContrastTextColor";
import { TimePicker } from "../../components/TimePicker/TimePicker";
import adjustTimeForMidnightCross from "../../utilities/helpers/adjustTimeForMidnightCross";
import useShiftTimes from "../../components/Hooks/useShiftTimes";
import { shiftDurationHours, shiftStartHour } from "../../offline/shiftBands";
import { usePlantJobTypes } from "../../components/Hooks/usePlantJobTypes";
import { ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import { plantAjax } from "../../ajax/PlantAjax";
import { stoppagesAjax } from "../../ajax/StoppagesAjax";
import { StoppageSubTypeOption } from "../../components/Hooks/useStoppageTypes";
import { PlantJobTypeDTO } from "../../DTOs/PlantJobTypeDTO";
import { StoppagePreset } from "../../DTOs/Stoppage/MultipleStoppages";
import { PlantLookupDTO } from "../../DTOs/Plants/PlantLookupDTO";
import { TripleShift } from "../../types/Shift";
import {
  addMultipleStoppagesOffline,
  availableMachinesFromCache,
} from "../../offline/multipleStoppagesOffline";
import { isOnlineNow } from "../../offline/connectivity";
import { vocabularyName } from "../../i18n/vocabulary";

interface MultipleStoppagesModalProps {
  selectedDate: DateTime;
  selectedShift: TripleShift;
  stoppageTypeGroupOptions: StoppageSubTypeOption[];
  closeModal: () => void;
  refreshPage: () => void;
}

type FilterState = {
  stoppageTypeId: number;
  stoppageSubTypeId: number;
  timeIn: DateTime;
  timeOut?: DateTime | null;
  jobTypes?: PlantJobTypeDTO[];
}

const MultipleStoppagesModal = (
  {
    selectedDate,
    selectedShift,
    stoppageTypeGroupOptions,
    closeModal,
    refreshPage
  }: MultipleStoppagesModalProps) => {
  const { t } = useTranslation("production");
  const [isLoading, setIsLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const {shiftStartTime: rawStart, shiftEndTime: rawEnd} = useShiftTimes();
  // Note: the afternoon band's hours come from the configured ShiftBands rather than the literal 14 and
  // 22 that used to sit here, so re-timing or re-lengthening the afternoon shift in appsettings moves
  // these pickers with it instead of leaving them clamped to the old window.
  const afternoonStartHour = shiftStartHour("A");
  const shiftStartTime = selectedShift[0] === "A"
    ? selectedDate.set({ hour: afternoonStartHour, minute: 0 })
    : rawStart;

  const shiftEndTime = selectedShift[0] === "A"
    ? shiftStartTime.plus({ hours: shiftDurationHours("A") })
    : rawEnd;

  // Note: the picker only chooses a time; the date it hands back is today's or the previous value's, not the shift's.
  const onShiftDate = (picked?: DateTime | null) =>
    picked ? adjustTimeForMidnightCross(picked, selectedDate, shiftStartTime, shiftEndTime) : undefined;

  const [filterState, setFilterState] = useState<FilterState>({
    stoppageTypeId: 0,
    stoppageSubTypeId: 0,
    timeIn: shiftStartTime,
    timeOut: null,
  });

  const { data: stoppagePresets = [] } = useQuery({
    queryKey: ['stoppages', 'presets', selectedDate.toISODate(), selectedShift],
    queryFn: async () => {
      // TODO: the afternoon shift workaround.
      if (selectedShift === "AFTERNOON") return [] as StoppagePreset[];

      const res = await stoppagesAjax.getStoppagePresets(selectedDate, selectedShift);
      if (res instanceof ErrorResponse || !res) return [] as StoppagePreset[];
      return res;
    },
    enabled: selectedShift !== "AFTERNOON",
  });

  const plantJobTypes = usePlantJobTypes();

  const jobTypesEnum = filterState.jobTypes && filterState.jobTypes.length > 0
    ? filterState.jobTypes.reduce((acc, jt) => acc | jt.value, 0)
    : undefined;

  const { data: plants = [] } = useQuery({
    queryKey: [
      'plants', 'multipleStoppage', selectedDate.toISODate(), selectedShift,
      filterState.timeIn?.toISO(), filterState.timeOut?.toISO() ?? null, jobTypesEnum
    ],
    queryFn: async (): Promise<PlantLookupDTO[]> => {
      if (!isOnlineNow()) return availableMachinesFromCache(selectedDate, selectedShift, jobTypesEnum);

      const res = await plantAjax.getMultipleStoppagePlants(
        selectedDate, selectedShift, filterState.timeIn!, filterState.timeOut ?? undefined, jobTypesEnum
      );
      if (!res.success || !res.data) return [];
      return res.data;
    },
    enabled: filterState.stoppageSubTypeId !== 0
      && filterState.stoppageTypeId !== 0
      && filterState.timeIn !== undefined,
  });

  // Note: null means the crew has not chosen, so every listed machine is selected. Once they have, a
  // changed list must not choose for them again, and only the chosen machines still listed are saved.
  const [chosenIds, setChosenIds] = useState<number[] | null>(null);
  const machineIds = chosenIds === null
    ? plants.map(p => p.id)
    : chosenIds.filter(id => plants.some(p => p.id === id));

  const toggleSelection = (id: number) => {
    const current = chosenIds ?? plants.map(p => p.id);
    setChosenIds(current.includes(id) ? current.filter(x => x !== id) : [...current, id]);
  };

  const addMultipleStoppages = async () => {
    if (filterState.stoppageSubTypeId === 0 || filterState.stoppageTypeId === 0) {
      enqueueSnackbar(t("multipleStoppages.typeRequired"), { variant: 'error', autoHideDuration: 3000 });
    }

    if (machineIds.length === 0) {
      enqueueSnackbar(t("multipleStoppages.plantsRequired"), { variant: 'error', autoHideDuration: 3000 });
    }

    setIsLoading(true);
    try {
      if (!isOnlineNow()) {
        const added = await addMultipleStoppagesOffline({
          machineIds,
          machines: plants,
          stoppageTypeId: filterState.stoppageTypeId,
          stoppageSubTypeId: filterState.stoppageSubTypeId,
          timeIn: filterState.timeIn,
          timeOut: filterState.timeOut ?? undefined,
          subType: stoppageTypeGroupOptions.find(o => o.value === filterState.stoppageSubTypeId),
          selectedDate,
          selectedShift,
        });

        enqueueSnackbar(t("multipleStoppages.savedOffline", { count: added }), { variant: 'info', autoHideDuration: 5000 })
        closeModal();
        refreshPage();
        return true;
      }

      const response = await stoppagesAjax.insertMultipleStoppages({
        stoppageTypeId: filterState.stoppageTypeId,
        stoppageSubTypeId: filterState.stoppageSubTypeId,
        timeIn: filterState.timeIn,
        timeOut: filterState.timeOut ?? undefined,
        machineIds
      });

      if (!response.success && !response.data) {
        response.errors?.forEach((error, index) => {
          enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 + index * 1500 })
        })

        return false;
      }

      enqueueSnackbar(t("multipleStoppages.added"), { variant: 'success', autoHideDuration: 5000 })
      closeModal();
      refreshPage();

    } catch (error) {
      let message = t("multipleStoppages.addFailed");

      if (error instanceof Error) {
        message = error.message;
      } else if (typeof error === "string") {
        message = error;
      }

      enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 })
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <form className="space-y-6 text-theme-text">
      <h2 className="text-2xl pb-2 font-semibold border-b border-theme-border">{t("multipleStoppages.title")}</h2>

      <div className="flex flex-wrap gap-2">
        {stoppagePresets.map(preset => (
          <button
            key={preset.name}
            type="button"
            className="px-3 py-1 rounded bg-theme-info text-white font-medium hover:bg-theme-info-hover transition-colors cursor-pointer"
            onClick={() => {
              setFilterState(prev => ({
                ...prev,
                timeIn: preset.timeIn,
                timeOut: preset.timeOut,
                stoppageSubTypeId: preset.stoppageSubTypeId,
                stoppageTypeId: preset.stoppageTypeId,
              }));
              setChosenIds(null);
            }}
          >
            {preset.name}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="font-medium mb-1 block">{t("multipleStoppages.stoppageType")}</label>
          <Autocomplete
            size="small"
            options={stoppageTypeGroupOptions}
            value={stoppageTypeGroupOptions.find(o => o.value === filterState.stoppageSubTypeId) ?? null}
            getOptionLabel={(option) => vocabularyName("stoppageSubTypes", option.label)}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            groupBy={(option) => option.stoppageType}
            renderGroup={(params) => {
              const groupColor = stoppageTypeGroupOptions.find(o => o.stoppageType === params.group)?.groupColor;
              const contrast = getContrastTextColor(groupColor!);
              return (
                <div key={params.key}>
                  <div className="px-3 py-1 font-semibold text-sm text-white"
                    style={{ backgroundColor: groupColor, color: contrast }}
                  >
                    {vocabularyName("stoppageTypes", params.group)}
                  </div>
                  <ul className="bg-white border border-theme-border divide-y">{params.children}</ul>
                </div>
              );
            }}
            onChange={(e, newValue) => {
              setFilterState(prev => ({
                ...prev,
                stoppageSubTypeId: newValue?.value ?? 0,
                stoppageTypeId: newValue?.downtimeTypeId ?? 0
              }));
            }}
            renderInput={(params) => (
              <TextField {...params} label={t("multipleStoppages.stoppageType")} fullWidth />
            )}
          />
        </div>

        <div className="flex gap-4">
          <div className="flex-1">
            <label className="block font-medium mb-1">{t("columns.timeIn")}</label>
            <TimePicker
              initialDate={filterState.timeIn}
              onChange={(e) => setFilterState(prev => ({ ...prev, timeIn: onShiftDate(e) ?? prev.timeIn }))}
              minValue={shiftStartTime}
              maxValue={shiftEndTime}
            />
          </div>
          <div className="flex-1">
            <label className="block font-medium mb-1">{t("columns.timeOut")}</label>
            <TimePicker
              initialDate={filterState.timeOut}
              onChange={(e) => setFilterState(prev => ({ ...prev, timeOut: onShiftDate(e) }))}
              minValue={shiftStartTime}
              maxValue={shiftEndTime}
              clearable={true}
            />
          </div>
        </div>
      </div>

      <div className="flex-1">
        <label className="block font-medium mb-1">{t("multipleStoppages.jobTypes")}</label>
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={plantJobTypes}
          getOptionLabel={(option) => vocabularyName("jobTypes", option.label)}
          value={filterState.jobTypes}
          size="small"
          classes={{ root: 'w-full' }}
          onChange={(_, newValue) => setFilterState(prev => ({ ...prev, jobTypes: newValue }))}
          renderInput={(params) => <TextField {...params} label={t("multipleStoppages.jobTypes")} />}
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="font-medium">{t("multipleStoppages.selectMachines")}</label>
          <button
            type="button"
            className="text-sm text-theme-info hover:underline"
            onClick={() => {
              const allSelected = plants.every(p => machineIds.includes(p.id));
              setChosenIds(allSelected ? [] : plants.map(p => p.id));
            }}
          >
            {plants.every(p => machineIds.includes(p.id)) ? t("multipleStoppages.deselectAll") : t("multipleStoppages.selectAll")}
          </button>
        </div>

        <div className="max-h-40 overflow-y-auto border rounded p-2">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            {plants.map(item => (
              <label key={item.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={machineIds.includes(item.id)}
                  onChange={() => toggleSelection(item.id)}
                />
                <span>{item.plantRef}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <ModalActions className="!mt-0 !pt-4">
        <Button type="button" variant="secondary" onClick={closeModal}>
          {t("actions.cancel")}
        </Button>
        <Button
          type="submit"
          variant="info"
          isDefaultAction
          mnemonic="a"
          isLoading={isLoading}
          onClick={addMultipleStoppages}
        >
          {t("actions.add")}
        </Button>
      </ModalActions>
    </form>
  );
};

export default MultipleStoppagesModal;
